import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Search, ShieldCheck, X } from "lucide-react";
import { api, pesanGalat } from "../../lib/api";
import LoadingState from "@/components/ui/loading-state";

/**
 * Pengaturan "siapa melihat modal & margin" di Price List — brief owner
 * EppleHub 18 Sep 2026 (memory/BRIEF-2026-09-18.md).
 *
 * Hanya dibuka superadmin/admin: tombolnya digerbangi `bisa_atur_akses` dari
 * server, dan endpoint `/inventory/price-list/akses` menolak role lain 403.
 *
 * Siapa yang BENAR-BENAR melihat dihitung SERVER (fungsi yang sama dengan
 * penegaknya), bukan ditebak di sini — memilih seseorang tidak otomatis berarti
 * ia melihat (akun tanpa izin halaman Price List, kolom yang dimatikan).
 * Ringkasannya selalu menggambarkan pengaturan TERSIMPAN, bukan centang yang
 * belum disimpan.
 */
const KOLOM = [
  { k: "modal", label: "Modal", ket: "Modal per unit dan nilai persediaan." },
  { k: "margin", label: "Margin", ket: "Margin (%) dan tanda rugi." },
];

const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
// Cermin `antrian.jabatan_dasar` + casefold: server mencocokkan jabatan TANPA
// membedakan huruf besar-kecil dan tanpa grade. Kalau layar membandingkan teks
// persis, jabatan tersimpan "Frontliner" tampil tak tercentang di baris
// berlabel "FRONTLINER" padahal servernya memberi akses — dan tidak bisa dicabut.
const kunciJabatan = (s) => String(s || "").split("—")[0].replace(/\s+/g, " ").trim().toLowerCase();

const draftDari = (p) => ({
  tampil: p?.tampil !== false,
  mode: p?.mode || "izin_laporan",
  jabatan: [...(p?.jabatan || [])],
  akun: [...(p?.akun || [])],
});

export default function AksesModalPriceList({ onClose, onTersimpan }) {
  const [data, setData] = useState(null);
  const [galat, setGalat] = useState("");
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState("modal");
  const [cari, setCari] = useState("");
  const [busy, setBusy] = useState(false);

  const terima = useCallback((d) => {
    setData(d);
    setDraft({ modal: draftDari(d?.pengaturan?.modal), margin: draftDari(d?.pengaturan?.margin) });
  }, []);

  useEffect(() => {
    api.get("/inventory/price-list/akses")
      .then((r) => terima(r.data))
      .catch((e) => setGalat(pesanGalat(e, "Gagal memuat pengaturan akses")));
  }, [terima]);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [busy, onClose]);

  const akun = useMemo(() => data?.akun || [], [data]);
  const jabatan = useMemo(() => data?.jabatan || [], [data]);
  const akunTampil = useMemo(() => {
    const q = cari.trim().toLowerCase();
    if (!q) return akun;
    return akun.filter((a) => [a.nama, a.email, a.jabatan, a.cabang, a.role]
      .some((v) => String(v || "").toLowerCase().includes(q)));
  }, [akun, cari]);

  const ubah = (k, medan, nilai) => setDraft((d) => ({ ...d, [k]: { ...d[k], [medan]: nilai } }));
  const toggle = (k, medan, nilai) => setDraft((d) => {
    const lama = d[k][medan];
    return { ...d, [k]: { ...d[k], [medan]: lama.includes(nilai) ? lama.filter((x) => x !== nilai) : [...lama, nilai] } };
  });
  const jabatanTercentang = (k, jab) => (draft?.[k]?.jabatan || []).some((x) => kunciJabatan(x) === kunciJabatan(jab));
  const toggleJabatan = (k, jab) => setDraft((d) => {
    const lama = d[k].jabatan;
    const ada = lama.some((x) => kunciJabatan(x) === kunciJabatan(jab));
    // Melepas membuang SEMUA ejaan jabatan yang sama, bukan hanya yang persis.
    return { ...d, [k]: { ...d[k], jabatan: ada ? lama.filter((x) => kunciJabatan(x) !== kunciJabatan(jab)) : [...lama, jab] } };
  });

  // Hanya kolom yang BENAR-BENAR diubah yang dikirim: dua admin yang
  // menyunting kolom berbeda tidak saling menimpa dengan salinan basi.
  const berubah = useMemo(() => {
    if (!draft || !data) return {};
    const hasil = {};
    KOLOM.forEach(({ k }) => {
      if (JSON.stringify(draft[k]) !== JSON.stringify(draftDari(data.pengaturan?.[k]))) hasil[k] = draft[k];
    });
    return hasil;
  }, [draft, data]);
  const adaPerubahan = Object.keys(berubah).length > 0;

  const simpan = async () => {
    if (!adaPerubahan) { toast.info("Tidak ada perubahan"); return; }
    setBusy(true);
    try {
      const { data: r } = await api.put("/inventory/price-list/akses", berubah);
      toast.success(r.message || "Akses modal & margin disimpan");
      terima(r);
      if (onTersimpan) onTersimpan();
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal menyimpan pengaturan akses"));
    } finally {
      setBusy(false);
    }
  };

  const ring = data?.ringkasan;
  const d = draft?.[tab];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => !busy && onClose()}
         data-testid="pl-akses-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" /> Akses Modal & Margin
            </h3>
            <p className="text-xs text-slate-500">
              Atur apakah kolom modal dan margin tampil di Price List, dan siapa saja yang boleh melihatnya.
              Berlaku di Price List (layar & Excel) dan Master Produk (daftar, detail, ubah harga massal).
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={busy} data-testid="pl-akses-tutup"
            className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {galat && <p className="text-sm text-rose-700" data-testid="pl-akses-galat">{galat}</p>}
          {!data && !galat && <LoadingState />}
          {data && d && (
            <>
              <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                {KOLOM.map((c) => (
                  <button key={c.k} type="button" onClick={() => setTab(c.k)}
                    data-testid={`pl-akses-tab-${c.k}`}
                    className={`px-4 h-9 text-sm ${tab === c.k ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                    {c.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500">{KOLOM.find((c) => c.k === tab)?.ket}</p>

              <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                <input type="checkbox" checked={d.tampil} data-testid={`pl-akses-tampil-${tab}`}
                  onChange={(e) => ubah(tab, "tampil", e.target.checked)} />
                Tampilkan kolom {tab === "modal" ? "modal" : "margin"} di Price List
              </label>
              {!d.tampil && (
                <p className="text-xs text-amber-700">
                  Kolom ini tidak ditampilkan kepada siapa pun di Price List, termasuk superadmin, sampai
                  dinyalakan lagi. Di Master Produk hanya superadmin yang tetap melihatnya (untuk mengelola
                  harga beli).
                </p>
              )}

              {d.tampil && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">Siapa yang boleh melihat</p>
                  {[
                    { v: "izin_laporan", l: `Semua pemegang izin "${data.label_izin_laporan || "Laporan Stok"}" (bawaan)` },
                    { v: "pilihan", l: "Hanya jabatan & akun yang dipilih di bawah" },
                  ].map((o) => (
                    <label key={o.v} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input type="radio" name={`mode-${tab}`} checked={d.mode === o.v}
                        data-testid={`pl-akses-mode-${tab}-${o.v}`}
                        onChange={() => ubah(tab, "mode", o.v)} />
                      {o.l}
                    </label>
                  ))}
                  <p className="text-xs text-slate-500">Superadmin selalu bisa melihat selama kolom ini ditampilkan.</p>
                </div>
              )}

              {d.tampil && d.mode === "pilihan" && (
                <>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">Per jabatan</p>
                    <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 max-h-48 overflow-y-auto">
                      {jabatan.length === 0 && (
                        <li className="px-3 py-2 text-xs text-slate-400">Belum ada data jabatan karyawan.</li>
                      )}
                      {jabatan.map((j) => (
                        <li key={j.jabatan_dasar} className="px-3 py-2 text-sm">
                          <label className="flex flex-wrap items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={jabatanTercentang(tab, j.jabatan_dasar)}
                              data-testid={`pl-akses-jabatan-${tab}-${slug(j.jabatan_dasar)}`}
                              onChange={() => toggleJabatan(tab, j.jabatan_dasar)} />
                            <span className={j.jumlah_karyawan === 0 ? "font-medium text-rose-700" : "font-medium text-slate-800"}>
                              {j.jabatan_dasar}
                            </span>
                            <span className="text-xs text-slate-500">
                              {j.jumlah_karyawan || 0} karyawan · {j.jumlah_punya_akun || 0} punya akun
                            </span>
                          </label>
                          {j.jumlah_karyawan === 0 && (
                            <p className="text-xs text-rose-700 mt-0.5 ml-6">
                              Tidak cocok dengan data karyawan mana pun
                              {(j.mirip || []).length > 0 && <> — mirip: {j.mirip.join(", ")}</>}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">Per akun</p>
                    <div className="relative">
                      <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
                      <input value={cari} onChange={(e) => setCari(e.target.value)}
                        placeholder="Cari nama, email, jabatan, cabang…" data-testid="pl-akses-cari"
                        className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-300 text-sm" />
                    </div>
                    <div className="overflow-x-auto rounded-lg border border-slate-200 max-h-72 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                          <tr>
                            <th className="px-3 py-2 w-8"></th>
                            <th className="text-left px-3 py-2">Akun</th>
                            <th className="text-left px-3 py-2">Jabatan · cabang</th>
                          </tr>
                        </thead>
                        <tbody>
                          {akunTampil.length === 0 && (
                            <tr><td colSpan={3} className="px-3 py-4 text-center text-xs text-slate-400">Tidak ada akun</td></tr>
                          )}
                          {akunTampil.map((a) => (
                            <tr key={a.id} className="border-t border-slate-100 align-top">
                              <td className="px-3 py-2">
                                <input type="checkbox" checked={d.akun.includes(a.id)}
                                  data-testid={`pl-akses-akun-${tab}-${a.id}`}
                                  onChange={() => toggle(tab, "akun", a.id)} />
                              </td>
                              <td className="px-3 py-2">
                                <p className="text-slate-800">{a.nama || "-"}</p>
                                <p className="text-xs text-slate-500 break-all">{a.email} · {a.role}</p>
                                {!a.akses_halaman && (
                                  <p className="text-xs text-amber-700">tidak punya akses halaman Price List</p>
                                )}
                                {a.karyawan_nonaktif && (
                                  <p className="text-xs text-rose-700">karyawan nonaktif — akunnya masih bisa masuk</p>
                                )}
                              </td>
                              <td className="px-3 py-2 text-xs text-slate-600">
                                {a.jabatan || "-"} · {a.cabang || "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {(data.akun_hilang?.[tab] || []).length > 0 && (
                      <p className="text-xs text-slate-500" data-testid="pl-akses-akun-hilang">
                        {data.akun_hilang[tab].length} akun terpilih sudah dihapus dari sistem — otomatis
                        dibuang saat disimpan.
                      </p>
                    )}
                  </div>
                </>
              )}

              {ring && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm space-y-1"
                     data-testid="pl-akses-ringkasan">
                  <p className="text-xs text-slate-500">Menurut pengaturan yang TERSIMPAN:</p>
                  <p>
                    Melihat modal: <b data-testid="pl-akses-n-modal">{ring.melihat_modal.length}</b> akun
                    {ring.melihat_modal.length > 0 && (
                      <span className="text-slate-500"> — {ring.melihat_modal.slice(0, 8).join(", ")}
                        {ring.melihat_modal.length > 8 && ` +${ring.melihat_modal.length - 8} lainnya`}</span>
                    )}
                  </p>
                  <p>
                    Melihat margin: <b data-testid="pl-akses-n-margin">{ring.melihat_margin.length}</b> akun
                    {ring.melihat_margin.length > 0 && (
                      <span className="text-slate-500"> — {ring.melihat_margin.slice(0, 8).join(", ")}
                        {ring.melihat_margin.length > 8 && ` +${ring.melihat_margin.length - 8} lainnya`}</span>
                    )}
                  </p>
                  {ring.margin_tanpa_modal.length > 0 && (
                    <p className="text-xs text-amber-700 flex items-start gap-1" data-testid="pl-akses-peringatan-margin">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>
                        {ring.margin_tanpa_modal.length} akun melihat margin tanpa modal
                        ({ring.margin_tanpa_modal.slice(0, 5).join(", ")}). Margin dan harga jual cukup untuk
                        menghitung modal (modal = harga jual × (1 − margin%)), jadi modal tidak benar-benar
                        tersembunyi dari mereka.
                      </span>
                    </p>
                  )}
                  {(ring.modal_tanpa_margin || []).length > 0 && (
                    <p className="text-xs text-amber-700 flex items-start gap-1" data-testid="pl-akses-peringatan-modal">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>
                        {ring.modal_tanpa_margin.length} akun melihat modal tanpa margin
                        ({ring.modal_tanpa_margin.slice(0, 5).join(", ")}). Modal dan harga jual cukup untuk
                        menghitung margin, jadi margin tidak benar-benar tersembunyi dari mereka.
                      </span>
                    </p>
                  )}
                  {(ring.cocok_tanpa_akses_halaman || []).length > 0 && (
                    <p className="text-xs text-amber-700">
                      Termasuk aturan tapi tidak bisa membuka Price List (butuh izin
                      "{data.label_izin_halaman || "Master Produk"} — lihat"):{" "}
                      {ring.cocok_tanpa_akses_halaman.join(", ")}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
            className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
            Tutup
          </button>
          <button type="button" onClick={simpan} disabled={busy || !draft || !adaPerubahan} data-testid="pl-akses-simpan"
            className="h-10 px-4 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60">
            {busy ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </div>
    </div>
  );
}
